import Client from '@spike/client-model';
import { CreateConversationInput, InboxContact } from '@spike/omnichannel-model';
import { realEmail } from '../contactEmail';

/** Who the new conversation is with: an existing client, or a raw phone the backend resolves. */
export type Recipient =
    | { kind: 'client'; id: number; name: string; phone?: string; email?: string }
    | { kind: 'phone'; phone: string };

const MIN_PHONE_DIGITS = 7;
/** POST /conversations and GET /conversations/lookup reject a phone under 10 digits with 422. */
const MIN_TEXTABLE_PHONE_DIGITS = 10;

const phoneShape = (text: string): string | undefined => {
    const trimmed = text.trim();
    if (!/^[+\d][\d\s().-]*$/.test(trimmed)) return undefined;
    return trimmed;
};

const digitCount = (text: string): number => text.replace(/\D/g, '').length;

/** True when the text reads as a phone number rather than a name/email search. */
export const looksLikePhone = (text: string): boolean => {
    const trimmed = phoneShape(text);
    return trimmed !== undefined && digitCount(trimmed) >= MIN_PHONE_DIGITS;
};

/** True once the phone is long enough for the backend to accept it. */
export const canTextPhone = (text: string): boolean => {
    const trimmed = phoneShape(text);
    return trimmed !== undefined && digitCount(trimmed) >= MIN_TEXTABLE_PHONE_DIGITS;
};

export const recipientFromClient = (client: Client): Recipient => ({
    kind: 'client',
    id: client.id!,
    name:
        [client.firstName, client.lastName].filter(Boolean).join(' ').trim() || realEmail(client.email) || client.phone,
    phone: client.phone || undefined,
    email: realEmail(client.email)
});

/** The Composer decides which channels are available from the contact's phone/email. */
export const recipientToContact = (recipient: Recipient): InboxContact =>
    recipient.kind === 'client'
        ? { type: 'customer', id: recipient.id, name: recipient.name, phone: recipient.phone, email: recipient.email }
        : { type: 'prospect', id: 0, name: recipient.phone, phone: recipient.phone };

export const recipientToCreateInput = (recipient: Recipient): CreateConversationInput =>
    recipient.kind === 'client'
        ? { contactType: 'customer', contactId: recipient.id }
        : { phone: recipient.phone.trim() };

export const phoneDigits = (phone?: string): string => (phone ?? '').replace(/\D/g, '').slice(-10);
